import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Size guide of the product page (`blocks/pc-pdp-size-guide.liquid`): a native dialog opened by any element with
 * `data-pc-size-guide-open` (the "Guía de tallas" link of the size row), with two tabs:
 *
 * - "Encuentra tu talla": the visitor sets the width of the hand (number or slider, in mm or inches) and gets the size
 *   of the model that matches it;
 * - "Tabla completa": the chart, drawn by the server with one row per size, and a button on every size the model makes.
 *
 * Everything the script needs comes from the chart rows: `data-mm` (the published width, empty when there is none),
 * `data-size` (the size of the product for that row, empty when the model does not make it) and `data-label` (the size
 * as the chart writes it, "8 (M)"). The messages come as templates in the `data-msg-*` attributes of the result.
 *
 * The opener is found with a delegated click on the document, because the variant picker that holds the link redraws
 * itself on every size change and would otherwise lose its listener. The dialog gives Escape, focus containment and
 * focus restoration; clicking the backdrop closes it too. The tabs and the finder are hidden until this script runs.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog.
 * @property {HTMLElement} [tablist] - The tab bar (hidden until the script runs).
 * @property {HTMLButtonElement[]} [tabs] - The tab buttons, with `data-tab`.
 * @property {HTMLElement[]} [panels] - The tab panels, with `data-panel`.
 * @property {HTMLInputElement} [input] - Number input of the width.
 * @property {HTMLInputElement} [range] - Slider of the width.
 * @property {HTMLButtonElement[]} [units] - The "mm" / "pulg" buttons, with `data-unit`.
 * @property {HTMLElement[]} [ticks] - Labels under the slider, with `data-mm`.
 * @property {HTMLElement} [result] - The result box (`data-state`, `data-msg-*`).
 * @property {HTMLElement} [resultSize] - The size shown big.
 * @property {HTMLElement} [resultText] - The sentence under it.
 * @property {HTMLButtonElement} [apply] - "Elegir talla X".
 * @property {HTMLTableRowElement[]} [rows] - The rows of the chart.
 *
 * @extends {Component<Refs>}
 */
class PcSizeGuide extends Component {
  requiredRefs = ['dialog'];

  /** @type {AbortController | undefined} */
  #abort;

  /** Width of the hand, always in mm. */
  #mm = 88;

  /** Unit shown in the number input: 'mm' or 'in'. */
  #unit = 'mm';

  #tab = 'find';

  /** Size of the product the result points to. */
  #resultSize = '';

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    document.addEventListener('click', this.#onDocumentClick, { signal: this.#abort.signal });

    this.#mm = this.#clamp(Number(this.dataset.widthDefault) || this.#mm);
    if (this.refs.tablist) this.refs.tablist.hidden = false;
    this.#showTab(this.#tab);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
  }

  updatedCallback() {
    super.updatedCallback();

    // A section render brings the server markup back: the tabs and the finder are hidden again
    if (this.refs.tablist) this.refs.tablist.hidden = false;
    this.#showTab(this.#tab);
    this.#render();
  }

  get #min() {
    return Number(this.dataset.widthMin) || 45;
  }

  get #max() {
    return Number(this.dataset.widthMax) || 160;
  }

  /** @param {number} value */
  #clamp(value) {
    return Math.min(this.#max, Math.max(this.#min, Math.round(value)));
  }

  /** @param {Event} event */
  #onDocumentClick = (event) => {
    const opener = event.target instanceof Element ? event.target.closest('[data-pc-size-guide-open]') : null;
    if (!opener) return;

    event.preventDefault();
    this.open();
  };

  /** Opens the dialog, marking the size that is chosen in the variant picker. */
  open() {
    if (this.refs.dialog.open) return;

    this.#syncSelection();
    this.refs.dialog.showModal();
  }

  /** Closes the dialog. Called by the close button. */
  close() {
    this.refs.dialog.close();
  }

  /**
   * A click on the backdrop lands on the dialog element itself, not on its panel. The framework reports the element
   * that has the handler as the target, so the real one is read from the event path.
   * @param {Event} event
   */
  handleBackdropClick(event) {
    if (event.composedPath()[0] === this.refs.dialog) this.refs.dialog.close();
  }

  /**
   * Shows the tab of the clicked button. Called by the tab buttons.
   * @param {Event} event
   */
  showTab(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-tab]') : null;
    if (button instanceof HTMLElement && button.dataset.tab) this.#showTab(button.dataset.tab);
  }

  /**
   * Arrow keys, Home and End move between the tabs. Called by the tab bar.
   * @param {KeyboardEvent} event
   */
  handleTabKeydown(event) {
    const tabs = this.refs.tabs ?? [];
    const current = tabs.findIndex((tab) => tab.dataset.tab === this.#tab);
    if (current < 0) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    /** @type {Record<string, number>} */
    const moves = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 };
    let next = current;

    if (event.key in moves) next = (current + moves[event.key] + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    const tab = tabs[next];
    if (tab.dataset.tab) this.#showTab(tab.dataset.tab);
    tab.focus();
  }

  /** @param {string} name */
  #showTab(name) {
    this.#tab = name;

    for (const tab of this.refs.tabs ?? []) {
      const selected = tab.dataset.tab === name;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }

    // Without the tab bar (no script) only the chart shows, so the panels follow the tabs only once they are there
    for (const panel of this.refs.panels ?? []) {
      panel.hidden = panel.dataset.panel !== name;
    }
  }

  /**
   * Sets the width from the number input or the slider. Called by both.
   * @param {Event} event
   */
  setWidth(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const value = parseFloat(input.value.replace(',', '.'));
    if (Number.isNaN(value)) return;

    // The slider is always in mm; the number input follows the unit
    const mm = input === this.refs.range || this.#unit === 'mm' ? value : value * 25.4;
    this.#mm = this.#clamp(mm);
    this.#render();
  }

  /**
   * Switches the unit of the number input. Called by the unit buttons.
   * @param {Event} event
   */
  setUnit(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-unit]') : null;
    if (!(button instanceof HTMLElement) || !button.dataset.unit) return;

    this.#unit = button.dataset.unit;
    this.#render();
  }

  /** Chooses the size the result points to. Called by the "Elegir talla" button. */
  applyResult() {
    if (this.#resultSize) this.#choose(this.#resultSize);
  }

  /**
   * Chooses the size of the button that was clicked. Called by the buttons of the chart.
   * @param {Event} event
   */
  pickSize(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-size]') : null;
    if (button instanceof HTMLElement && button.dataset.size) this.#choose(button.dataset.size);
  }

  /**
   * Picks the size in the variant picker (its own handler updates the price, the link and the button) and closes.
   * The picker redraws the "Guía de tallas" link that focus went back to, so focus is put on the new one once the
   * selection has settled.
   * @param {string} size
   */
  #choose(size) {
    const radio = this.#sizeRadios().find((input) => input.value === size);
    const section = this.closest('.shopify-section');

    if (radio && !radio.checked && section) {
      const wait = new AbortController();
      const restoreFocus = () => {
        wait.abort();
        setTimeout(() => {
          if (document.activeElement && document.activeElement !== document.body) return;
          section.querySelector('[data-pc-size-guide-open]')?.focus();
        }, 100);
      };

      section.addEventListener(
        StandardEvents.productSelect,
        (event) => {
          const { promise } = /** @type {{ promise?: Promise<unknown> }} */ (event);
          Promise.resolve(promise).catch(() => {}).then(restoreFocus);
        },
        { once: true, signal: wait.signal }
      );
      // No selection event (a picker that does not fetch): give up waiting
      setTimeout(() => wait.abort(), 5000);
    }

    if (radio && !radio.checked) radio.click();
    this.close();
  }

  /** @returns {HTMLInputElement[]} */
  #sizeRadios() {
    const scope = this.closest('.shopify-section') ?? document;
    return Array.from(scope.querySelectorAll('variant-picker input[type="radio"]'));
  }

  /** Marks, on the chart, the size chosen in the variant picker. */
  #syncSelection() {
    const current = this.#sizeRadios().find((input) => input.checked)?.value;

    for (const row of this.refs.rows ?? []) {
      const selected = Boolean(current) && row.dataset.size === current;
      row.toggleAttribute('data-selected', selected);

      const tag = row.querySelector('.pc-size-guide__selected');
      const pick = row.querySelector('.pc-size-guide__pick');
      if (tag instanceof HTMLElement) tag.hidden = !selected;
      if (pick instanceof HTMLElement) pick.hidden = selected;
    }
  }

  /** The rows of the chart as plain data. */
  #chart() {
    return (this.refs.rows ?? []).map((row) => ({
      row,
      mm: row.dataset.mm ? Number(row.dataset.mm) : null,
      size: row.dataset.size ?? '',
      label: row.dataset.label ?? '',
    }));
  }

  /**
   * The size of the model for a width: the one of the nearest published width, the nearest the model does make when
   * it does not make that one, or its biggest size when the width is past the published ones and that size has no
   * published width (4XL).
   *
   * @param {number} mm
   */
  #recommend(mm) {
    const chart = this.#chart();
    const published = chart.filter((entry) => entry.mm !== null);
    if (!published.length) return null;

    const distance = (/** @type {number} */ from) => Math.abs(from - mm);
    const nearest = published.reduce((best, entry) => (distance(entry.mm ?? 0) < distance(best.mm ?? 0) ? entry : best));

    const last = published[published.length - 1];
    const previous = published[published.length - 2];
    const halfStep = previous ? ((last.mm ?? 0) - (previous.mm ?? 0)) / 2 : 0;
    const beyond = chart.slice(chart.indexOf(last) + 1).find((entry) => entry.mm === null && entry.size);

    if (beyond && mm > (last.mm ?? 0) + halfStep) {
      return { state: 'over', entry: last, size: beyond.size, nearest: last };
    }

    if (nearest.size) {
      // A double size ("S/M") is not written in the chart row, which only has the numeric and the letter size
      const written = nearest.label.split(/[\s()]+/).includes(nearest.size);
      return { state: written ? 'exact' : 'dual', entry: nearest, size: nearest.size, nearest };
    }

    const available = published.filter((entry) => entry.size);
    if (!available.length) return { state: 'none', entry: nearest, size: '', nearest };

    const closest = available.reduce((best, entry) => (distance(entry.mm ?? 0) < distance(best.mm ?? 0) ? entry : best));
    return { state: 'near', entry: nearest, size: closest.size, nearest };
  }

  /** Draws the width, the ticks, the result and the highlighted row. */
  #render() {
    const { input, range, units, ticks, result, resultSize, resultText, apply } = this.refs;
    if (!result) return;

    const inches = this.#unit === 'in';
    if (input) {
      input.step = inches ? '0.1' : '1';
      input.min = String(inches ? Math.round((this.#min / 25.4) * 10) / 10 : this.#min);
      input.max = String(inches ? Math.round((this.#max / 25.4) * 10) / 10 : this.#max);
      input.value = String(inches ? Math.round((this.#mm / 25.4) * 10) / 10 : this.#mm);
    }
    if (range) range.value = String(this.#mm);
    for (const button of units ?? []) button.setAttribute('aria-pressed', String(button.dataset.unit === this.#unit));

    const found = this.#recommend(this.#mm);
    const rows = this.#chart();
    for (const entry of rows) entry.row.toggleAttribute('data-hit', Boolean(found) && entry.row === found?.entry.row);
    for (const tick of ticks ?? []) {
      tick.toggleAttribute('data-active', Boolean(found) && Number(tick.dataset.mm) === found?.nearest.mm);
    }

    this.#resultSize = found?.size ?? '';
    result.dataset.state = found?.state ?? 'none';

    if (!found || !found.size) {
      if (resultSize) resultSize.textContent = '';
      if (resultText) resultText.textContent = '';
      if (apply) apply.hidden = true;
      return;
    }

    const template = result.dataset[`msg${found.state[0].toUpperCase()}${found.state.slice(1)}`] ?? '';
    const text = template.replace(/\{(\w+)\}/g, (_, key) => String({ mm: this.#mm, chart: found.entry.label, size: found.size }[key] ?? ''));
    if (resultSize) resultSize.textContent = found.size;
    if (resultText) resultText.textContent = text;

    if (apply) {
      apply.hidden = false;
      apply.textContent = (result.dataset.msgApply ?? '').replace(/\{size\}/g, () => found.size);
    }
  }
}

if (!customElements.get('pc-size-guide')) {
  customElements.define('pc-size-guide', PcSizeGuide);
}
